import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../hooks/useAdminAuth';

const NAV_ITEMS = [
  { to: '/admin', end: true,              icon: '📊', label: 'Dashboard' },
  { to: '/admin/courses',                 icon: '📚', label: 'Corsi' },
  { to: '/admin/editions',                icon: '📅', label: 'Edizioni' },
  { to: '/admin/users',                   icon: '👤', label: 'Iscritti' },
  { to: '/admin/enrollments',             icon: '📋', label: 'Iscrizioni' },
  { to: '/admin/enrollments/manual',      icon: '✏️',  label: 'Iscrivi manualmente' },
  { to: '/admin/speakers',               icon: '🎤', label: 'Relatori' },
  { to: '/admin/check-in',               icon: '🪪', label: 'Presenze' },
  { to: '/admin/2fa',                    icon: '🔐', label: 'Sicurezza' },
];

// Bottom nav shows only the 3 most-used items + hamburger
const BOTTOM_NAV = [
  { to: '/admin/users',       icon: '👤', label: 'Iscritti' },
  { to: '/admin/enrollments', icon: '📋', label: 'Iscrizioni' },
  { to: '/admin/courses',     icon: '📚', label: 'Corsi' },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, admin, logout } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/admin/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Close sidebar when navigating
  useEffect(() => {
    setSidebarOpen(false);
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="loading-container" style={{ minHeight: '100vh' }}>
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="admin-layout">
      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="admin-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`admin-sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="admin-sidebar-logo">
          <h2>TUPLA LMS</h2>
          <span>Pannello di amministrazione</span>
        </div>

        <nav className="admin-nav">
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <span>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <div style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
            Connesso come <strong style={{ color: 'rgba(255,255,255,0.9)' }}>{admin?.username}</strong>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ color: 'rgba(255,255,255,0.75)', width: '100%' }} onClick={logout}>
            Esci
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="admin-main">
        {/* Top bar */}
        <header className="admin-topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* Hamburger — visible only on mobile */}
            <button
              className="admin-hamburger"
              onClick={() => setSidebarOpen(v => !v)}
              aria-label="Menu"
            >
              <span /><span /><span />
            </button>
            <span style={{ color: 'var(--gray-500)', fontSize: '0.9375rem' }}>Area Amministrativa</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--success)', display: 'inline-block' }} />
              {admin?.username}
            </span>
            <button onClick={logout} className="btn btn-ghost btn-sm">Esci</button>
          </div>
        </header>

        {/* Page content */}
        <div className="admin-content">
          <Outlet />
        </div>
      </div>

      {/* Mobile bottom nav */}
      <nav className="admin-bottom-nav">
        {BOTTOM_NAV.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `admin-bottom-nav-item${isActive ? ' active' : ''}`}
          >
            <span className="admin-bottom-nav-icon">{item.icon}</span>
            <span className="admin-bottom-nav-label">{item.label}</span>
          </NavLink>
        ))}
        <button
          className="admin-bottom-nav-item"
          onClick={() => setSidebarOpen(v => !v)}
        >
          <span className="admin-bottom-nav-icon">☰</span>
          <span className="admin-bottom-nav-label">Altro</span>
        </button>
      </nav>
    </div>
  );
}
